export default function SafetyHarborDestinationsSection() {
  const stats = [
    {
      value: "45%",
      label: "of Safety Harbor requests are Tampa professional travel with under 3 weeks notice",
    },
    {
      value: "2–3 weeks",
      label: "average notice for Tampa medical conference and business meeting travel",
    },
    {
      value: "Mar–Aug",
      label: "peak travel season — passport renewals spike before spring break and summer trips",
    },
    {
      value: "48-hour",
      label: "shortest business departure window we've accommodated for a Safety Harbor professional",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common leisure destination for Safety Harbor families — whether by cruise from Port Tampa Bay or by direct flight from TPA to Nassau, Jamaica, or the Cayman Islands. Families from Safety Harbor's quiet neighborhoods regularly sail to the Western Caribbean and Eastern Caribbean. All cruise passengers and most air travelers need valid passports.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom & Ireland",
      context: "London, Edinburgh, and Dublin draw Tampa professionals for client meetings, medical conferences, and financial services engagements. British Airways operates direct TPA-to-London service, making the UK one of the most accessible European destinations for Safety Harbor travelers. Retirees also book British Isles cruises and Irish heritage trips.",
      urgency: "Business meeting invitations typically arrive 2–4 weeks before travel — insufficient time for standard passport processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico & Latin America",
      context: "Cancun, Riviera Maya, Los Cabos, and Puerto Vallarta are staple destinations for Safety Harbor families seeking beach vacations within a short flight from TPA. Business travelers fly to Mexico City for corporate meetings. Mexico requires six months of passport validity for entry, and cruise lines enforce this strictly at Tampa embarkation.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March for Safety Harbor families",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Germany, France & Switzerland",
      context: "Frankfurt (Lufthansa direct from TPA), Zurich (Edelweiss seasonal), and Paris (connecting service) draw Tampa medical professionals for conferences, pharmaceutical meetings, and European thermal spa tourism. Safety Harbor wellness travelers also book German and Austrian thermal spa circuits for health-focused European vacations.",
      urgency: "Medical conference registration deadlines are typically 4–6 weeks before the event — tight for standard processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Costa Rica, Bali & India",
      context: "Safety Harbor's wellness community travels to international yoga retreats, Ayurvedic centers, and eco-wellness destinations. Costa Rica draws yoga teacher trainees and wellness retreat participants. Bali hosts international yoga festivals and silent meditation retreats. India's Rishikesh and Kerala are pilgrimage sites for serious yoga practitioners from Safety Harbor's wellness scene.",
      urgency: "Wellness retreats typically require full payment 60–90 days in advance, but passport checks often happen only at final itinerary confirmation",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Toronto, Montreal, and Vancouver draw Tampa professionals for North American business meetings, and Safety Harbor retirees for summer rail trips and Niagara Falls visits. Toronto is particularly common for financial services and cross-border legal meetings. Canada requires six months of passport validity, and entry rules have tightened significantly for air travelers.",
      urgency: "Cross-border business meetings are often scheduled with 1–2 weeks of notice — far too short for standard processing",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Safety Harbor Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Safety Harbor
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Safety Harbor travelers head to the Caribbean for cruises, Europe for business conferences and wellness tourism, and Latin America and Asia for retreats and medical travel. These are the destinations that create the most passport urgency for our Safety Harbor clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout the Tampa Bay area</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Tampa", href: "/cities/tampa" },
              { name: "St. Petersburg", href: "/cities/st-petersburg" },
              { name: "Clearwater", href: "/cities/clearwater" },
              { name: "Dunedin", href: "/cities/dunedin" },
              { name: "Palm Harbor", href: "/cities/palm-harbor" },
              { name: "Sarasota", href: "/cities/sarasota" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}